'use client'

import { ArrowLeft } from 'lucide-react'

import type { BookDetails } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Button, CarouselItem } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CardAction } from '../components'
import { BookCoverCard, ContentCarousel, ContentStats } from '../components'
import { Tag } from '../components/tag'

/** Shared layout for a book-collection detail page. Only the `cover` and `cta` slots differ between
 * the featured (library) variant and the my-book-collection variant. */
interface BookCollectionDetailViewProps {
  /** Active books in the collection (already filtered). Drives the carousel and the tag list. */
  books: { book: BookDetails }[]
  name: string
  description?: string | null
  /** Optional author/owner shown after the book count, e.g. "Disusun oleh: Username". */
  compiledBy?: string
  /** Back-navigation label + link. Omit both to hide the back button (e.g. the public showcase). */
  backLabel?: string
  renderBackLink?: RenderLink
  /** Optional read-only popularity row shown under the title. */
  stats?: { likes?: number | null; views?: number | null }
  /** Cover slot — e.g. a single image (featured) or a BookGalleryCard (my collection). */
  cover: React.ReactNode
  /** Call-to-action slot — e.g. a save button (featured) or edit/delete/share controls (my collection). */
  cta?: React.ReactNode
  /** Rendered in place of the carousel/tags when the collection has no books. */
  emptyState?: React.ReactNode
  /** Per-book card action. Omit to render static (non-actionable) covers, e.g. the public showcase. */
  renderBookAction?: (book: BookDetails) => CardAction
  onTagClick?: (type: 'author' | 'publisher' | 'level', id: string) => void
  className?: string
}

interface TagItem {
  id: string
  name: string
  type: 'author' | 'publisher' | 'level'
}

export function BookCollectionDetailView({
  books,
  name,
  description,
  compiledBy,
  backLabel,
  renderBackLink,
  stats,
  cover,
  cta,
  emptyState,
  renderBookAction,
  onTagClick,
  className,
}: BookCollectionDetailViewProps) {
  const seenTagKeys = new Set<string>()
  const allTags: TagItem[] = []

  for (const { book } of books) {
    for (const author of book.authors) {
      const key = `author-${author.id}`
      if (!seenTagKeys.has(key)) {
        seenTagKeys.add(key)
        allTags.push({ id: author.id, name: author.name, type: 'author' })
      }
    }
    if (book.publisher) {
      const key = `publisher-${book.publisher.id}`
      if (!seenTagKeys.has(key)) {
        seenTagKeys.add(key)
        allTags.push({ id: book.publisher.id, name: book.publisher.name, type: 'publisher' })
      }
    }
    if (book.level) {
      const key = `level-${book.level.id}`
      if (!seenTagKeys.has(key)) {
        seenTagKeys.add(key)
        allTags.push({ id: book.level.id, name: book.level.name, type: 'level' })
      }
    }
  }

  return (
    <main className={cn('srd:w-full srd:h-full srd:min-h-dvh', className)}>
      <div className='srd:w-full srd:max-w-[1200px] srd:mx-auto srd:flex srd:flex-col srd:gap-y-10 srd:px-4 srd:py-10'>
        {renderBackLink && (
          <div className='srd:flex srd:items-center'>
            <Button
              asChild
              variant='ghost'
              className={cn('srd:p-0 srd:text-neutral-white', typographyVariants({ variant: 'title-md' }))}
            >
              {renderBackLink({
                children: (
                  <>
                    <ArrowLeft className='srd:size-6 srd:mr-2' />
                    {backLabel}
                  </>
                ),
              })}
            </Button>
          </div>
        )}

        <section className='srd:grid srd:md:grid-cols-[auto_1fr] srd:grid-cols-1 srd:gap-x-6 srd:gap-y-6'>
          <div className='srd:md:w-[280px] srd:w-full'>{cover}</div>
          <div className='srd:flex srd:flex-col srd:gap-y-3 srd:text-neutral-white'>
            <h1 className={cn(typographyVariants({ variant: 'headline-sm' }))}>{name}</h1>
            <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-grey-60')}>
              {books.length} buku{compiledBy ? ` • Disusun oleh: ${compiledBy}` : ''}
            </p>
            {stats && <ContentStats likes={stats.likes} views={stats.views} />}
            {description && (
              <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:line-clamp-3')}>{description}</p>
            )}
            {cta}
          </div>
        </section>

        {books.length === 0 && emptyState ? (
          emptyState
        ) : (
          <>
            <ContentCarousel>
              {books.map((item) => (
                <CarouselItem key={item.book.id} className='srd:basis-auto'>
                  <BookCoverCard
                    id={item.book.id}
                    coverUrl={item.book.coverUrl}
                    action={renderBookAction ? renderBookAction(item.book) : undefined}
                  />
                </CarouselItem>
              ))}
            </ContentCarousel>

            {allTags.length > 0 && (
              <div className='srd:flex srd:flex-wrap srd:gap-2'>
                {allTags.map((tag) => (
                  <Tag
                    key={`${tag.type}-${tag.id}`}
                    label={tag.name}
                    onClick={onTagClick ? () => onTagClick(tag.type, tag.id) : undefined}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  )
}
